import { buildWaypointIndex, findWaypointPath } from "../ai/pathfind.js";
import { CAPSULE_HEIGHT_STAND, CAPSULE_RADIUS } from "../constants.js";
import { aabbOverlapStrict, containsPoint, type AABB } from "../math/aabb.js";
import type { Vec3 } from "../math/vec3.js";
import type {
  ExtractDef,
  LootSpawnDef,
  MapAddress,
  MapBox,
  MapDef,
  MapPoi,
  WaypointGraph,
} from "./types.js";
import { terrainHeightAt } from "./terrain.js";

/**
 * Pure, allocation-light queries over map data. Deterministic: no clocks or
 * randomness, every iteration order fixed by the map data itself — the AI and
 * the server both plan against these.
 */

/** Point-in-box test. Faces count as inside. */
export function pointInAABB(b: AABB, p: Vec3): boolean {
  return containsPoint(b, p);
}

/** The first extract zone containing `pos`, or `null`. */
export function pointInExtract(map: MapDef, pos: Vec3): ExtractDef | null {
  for (const e of map.extracts) {
    if (containsPoint(e.zone, pos)) return e;
  }
  return null;
}

/** The loot spawn with this id, or `null`. Ids arrive from clients. */
export function findLootSpawn(map: MapDef, id: string): LootSpawnDef | null {
  for (const l of map.lootSpawns ?? []) {
    if (l.id === id) return l;
  }
  return null;
}

/**
 * Id of the waypoint nearest to `pos` (3D); ties to the lower id; -1 for an
 * empty graph. The graph-shaped counterpart of `nearestNode` in
 * `ai/pathfind.ts`.
 */
export function nearestWaypoint(graph: WaypointGraph, pos: Vec3): number {
  let bestId = -1;
  let bestDistSq = Infinity;
  for (const n of graph.nodes) {
    const dx = n.pos.x - pos.x;
    const dy = n.pos.y - pos.y;
    const dz = n.pos.z - pos.z;
    const d = dx * dx + dy * dy + dz * dz;
    if (d < bestDistSq || (d === bestDistSq && n.id < bestId)) {
      bestDistSq = d;
      bestId = n.id;
    }
  }
  return bestId;
}

/**
 * A* over the waypoint graph in one call — builds a `WaypointIndex`, runs one
 * query, throws it away; the implementation lives in `ai/pathfind.ts`. For
 * tests and validation; anything asking repeatedly must build the index once
 * and call `findWaypointPath`. Returns the inclusive path, or `[]` when unknown
 * or unreachable (this wrapper's own convention).
 */
export function findPath(
  graph: WaypointGraph,
  fromId: number,
  toId: number,
): number[] {
  return findWaypointPath(buildWaypointIndex(graph), fromId, toId) ?? [];
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

const EPS = 1e-6;

/** How far below a spawn we insist on finding a surface, metres. */
const SPAWN_FLOOR_PROBE = 2;

/** Strict interior test — a point resting exactly on a face is NOT inside. */
function strictlyInside(b: AABB, p: Vec3): boolean {
  return (
    p.x > b.min.x + EPS &&
    p.x < b.max.x - EPS &&
    p.y > b.min.y + EPS &&
    p.y < b.max.y - EPS &&
    p.z > b.min.z + EPS &&
    p.z < b.max.z - EPS
  );
}

function boxInsideBounds(bounds: AABB, b: AABB): boolean {
  return containsPoint(bounds, b.min) && containsPoint(bounds, b.max);
}

/** Side of one XZ bucket in `bucketSolids`, metres. */
const BUCKET_M = 8;

/**
 * Buckets the solids by XZ footprint so a point query touches a neighbourhood.
 * A village is ~75k boxes and ~3.6k waypoints — a quarter of a billion
 * point-in-box tests otherwise, run three times per raid. Same answer (a box is
 * filed under every bucket its footprint overlaps), linear cost.
 */
function bucketSolids(
  solids: readonly MapBox[],
): (x: number, z: number) => readonly MapBox[] {
  let minX = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  for (const s of solids) {
    if (s.box.min.x < minX) minX = s.box.min.x;
    if (s.box.min.z < minZ) minZ = s.box.min.z;
    if (s.box.max.x > maxX) maxX = s.box.max.x;
  }
  const none: readonly MapBox[] = [];
  if (!Number.isFinite(minX)) return () => none;
  const nx = Math.floor((maxX - minX) / BUCKET_M) + 1;
  const buckets = new Map<number, MapBox[]>();
  for (const s of solids) {
    const i0 = Math.floor((s.box.min.x - minX) / BUCKET_M);
    const i1 = Math.floor((s.box.max.x - minX) / BUCKET_M);
    const j0 = Math.floor((s.box.min.z - minZ) / BUCKET_M);
    const j1 = Math.floor((s.box.max.z - minZ) / BUCKET_M);
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const key = j * nx + i;
        const list = buckets.get(key);
        if (list === undefined) buckets.set(key, [s]);
        else list.push(s);
      }
    }
  }
  return (x, z) => {
    const i = Math.floor((x - minX) / BUCKET_M);
    const j = Math.floor((z - minZ) / BUCKET_M);
    return buckets.get(j * nx + i) ?? none;
  };
}

/**
 * Static sanity check for a `MapDef`: human-readable problems, empty means
 * structurally sound. Runs as a unit test for every map, so only cheap and
 * certain checks — it cannot prove a map is fun or every room reachable.
 */
export function validateMap(map: MapDef): string[] {
  const problems: string[] = [];
  const bounds = map.bounds;

  if (
    bounds.min.x >= bounds.max.x ||
    bounds.min.y >= bounds.max.y ||
    bounds.min.z >= bounds.max.z
  ) {
    problems.push("bounds are degenerate (min must be < max on every axis)");
    return problems;
  }
  if (map.raidLengthSec <= 0) problems.push("raidLengthSec must be > 0");
  if (map.id.length === 0) problems.push("map id is empty");

  // --- Solids ------------------------------------------------------------
  for (let i = 0; i < map.solids.length; i++) {
    const b = (map.solids[i] as { box: AABB }).box;
    if (b.min.x >= b.max.x || b.min.y >= b.max.y || b.min.z >= b.max.z) {
      problems.push(`solid[${i}] is degenerate`);
    } else if (!boxInsideBounds(bounds, b)) {
      problems.push(`solid[${i}] pokes outside bounds`);
    }
  }

  // --- Terrain -----------------------------------------------------------
  const terrain = map.terrain;
  if (terrain !== undefined) {
    if (terrain.cellSize <= 0) problems.push("terrain.cellSize must be > 0");
    if (!Number.isInteger(terrain.width) || terrain.width < 2) {
      problems.push("terrain.width must be an integer >= 2");
    }
    if (!Number.isInteger(terrain.depth) || terrain.depth < 2) {
      problems.push("terrain.depth must be an integer >= 2");
    }
    const vertexCount = terrain.width * terrain.depth;
    const quadCount = (terrain.width - 1) * (terrain.depth - 1);
    if (terrain.heights.length !== vertexCount) {
      problems.push(`terrain has ${terrain.heights.length} heights, expected ${vertexCount}`);
    }
    if (terrain.surfaceIds.length !== quadCount) {
      problems.push(`terrain has ${terrain.surfaceIds.length} surface ids, expected ${quadCount}`);
    }
    if (terrain.surfaces.length === 0) problems.push("terrain has no surfaces");
    for (let i = 0; i < terrain.surfaceIds.length; i++) {
      const id = terrain.surfaceIds[i] as number;
      if (!Number.isInteger(id) || id < 0 || id >= terrain.surfaces.length) {
        problems.push(`terrain.surfaceIds[${i}] is outside the palette`);
        break;
      }
    }
    const maxX = terrain.origin.x + (terrain.width - 1) * terrain.cellSize;
    const maxZ = terrain.origin.z + (terrain.depth - 1) * terrain.cellSize;
    if (
      terrain.origin.x < bounds.min.x - EPS ||
      terrain.origin.z < bounds.min.z - EPS ||
      maxX > bounds.max.x + EPS ||
      maxZ > bounds.max.z + EPS
    ) {
      problems.push("terrain pokes outside bounds");
    }
    for (let i = 0; i < terrain.heights.length; i++) {
      const h = terrain.heights[i] as number;
      if (!Number.isFinite(h) || h < bounds.min.y || h > bounds.max.y) {
        problems.push(`terrain.heights[${i}] is outside bounds or non-finite`);
        break;
      }
    }
    // Cut-out quads. Sortedness is not cosmetic: `terrainQuadIsHole` binary
    // searches this array on every capsule tick.
    if (terrain.holes !== undefined) {
      for (let k = 0; k < terrain.holes.length; k++) {
        const q = terrain.holes[k] as number;
        if (!Number.isInteger(q) || q < 0 || q >= quadCount) {
          problems.push(`terrain.holes[${k}] is outside the quad range`);
          break;
        }
      }
      for (let k = 1; k < terrain.holes.length; k++) {
        if ((terrain.holes[k] as number) <= (terrain.holes[k - 1] as number)) {
          problems.push("terrain.holes is not strictly increasing");
          break;
        }
      }
    }
  }

  // --- Player spawns -----------------------------------------------------
  if (map.playerSpawns.length === 0) problems.push("map has no playerSpawns");
  for (let i = 0; i < map.playerSpawns.length; i++) {
    const sp = map.playerSpawns[i] as Vec3;
    if (!containsPoint(bounds, sp)) {
      problems.push(`playerSpawn[${i}] is outside bounds`);
      continue;
    }
    // Everything the standing capsule would occupy.
    const capsule: AABB = {
      min: { x: sp.x - CAPSULE_RADIUS, y: sp.y, z: sp.z - CAPSULE_RADIUS },
      max: {
        x: sp.x + CAPSULE_RADIUS,
        y: sp.y + CAPSULE_HEIGHT_STAND,
        z: sp.z + CAPSULE_RADIUS,
      },
    };
    let blocked = false;
    const terrainFloor = map.terrain === undefined ? null : terrainHeightAt(map.terrain, sp.x, sp.z);
    let hasFloor =
      terrainFloor !== null &&
      terrainFloor <= sp.y + 1e-3 &&
      terrainFloor >= sp.y - SPAWN_FLOOR_PROBE;
    for (const s of map.solids) {
      const b = s.box;
      const isUnderfoot = b.max.y <= sp.y + EPS;
      if (
        isUnderfoot &&
        b.max.y >= sp.y - SPAWN_FLOOR_PROBE &&
        sp.x >= b.min.x &&
        sp.x <= b.max.x &&
        sp.z >= b.min.z &&
        sp.z <= b.max.z
      ) {
        hasFloor = true;
      }
      if (!isUnderfoot && !blocked && aabbOverlapStrict(capsule, b)) {
        blocked = true;
      }
    }
    if (blocked) problems.push(`playerSpawn[${i}] is buried in a solid`);
    if (!hasFloor) {
      problems.push(
        `playerSpawn[${i}] has no floor within ${SPAWN_FLOOR_PROBE} m below it`,
      );
    }
  }

  // --- Extracts ----------------------------------------------------------
  if (map.extracts.length === 0) problems.push("map has no extracts");
  const extractIds = new Set<string>();
  for (const e of map.extracts) {
    if (extractIds.has(e.id)) problems.push(`duplicate extract id "${e.id}"`);
    extractIds.add(e.id);
    if (e.dwellSec <= 0) problems.push(`extract "${e.id}" has dwellSec <= 0`);
    const z = e.zone;
    if (z.min.x >= z.max.x || z.min.y >= z.max.y || z.min.z >= z.max.z) {
      problems.push(`extract "${e.id}" has a degenerate zone`);
    } else if (!boxInsideBounds(bounds, z)) {
      problems.push(`extract "${e.id}" zone is not inside bounds`);
    }
  }

  // --- POIs --------------------------------------------------------------
  const pois = map.pois ?? [];
  for (let i = 0; i < pois.length; i++) {
    const p = pois[i] as MapPoi;
    if (p.name.length === 0) problems.push(`poi[${i}] has an empty name`);
    if (!containsPoint(bounds, p.pos)) {
      problems.push(`poi[${i}] "${p.name}" is outside bounds`);
    }
  }

  // --- Addresses: no street, no number, or a door outside the map is a plate
  // the
  // server would search for a quest spawn and never find. Whether the address
  // names a real building is not checked — `pos` IS the answer.
  const addresses = map.addresses ?? [];
  for (let i = 0; i < addresses.length; i++) {
    const a = addresses[i] as MapAddress;
    if (a.street.length === 0) problems.push(`address[${i}] has an empty street`);
    if (a.number.length === 0) problems.push(`address[${i}] has an empty number`);
    if (!containsPoint(bounds, a.pos)) {
      problems.push(`address[${i}] "${a.street} ${a.number}" is outside bounds`);
    }
  }

  // --- Loot spawns: `table` is checked for being a non-empty string only.
  // Whether it EXISTS is content, and `maps/` must not import `items/`; the
  // server asserts at boot.
  const lootSpawns = map.lootSpawns ?? [];
  const lootIds = new Set<string>();
  for (let i = 0; i < lootSpawns.length; i++) {
    const l = lootSpawns[i] as LootSpawnDef;
    if (l.id.length === 0) problems.push(`lootSpawn[${i}] has an empty id`);
    if (lootIds.has(l.id)) problems.push(`duplicate lootSpawn id "${l.id}"`);
    lootIds.add(l.id);
    if (l.table.length === 0) problems.push(`lootSpawn "${l.id}" has an empty table`);
    const b = l.box;
    if (b.min.x >= b.max.x || b.min.y >= b.max.y || b.min.z >= b.max.z) {
      problems.push(`lootSpawn "${l.id}" has a degenerate box`);
    } else if (!boxInsideBounds(bounds, b)) {
      problems.push(`lootSpawn "${l.id}" box is not inside bounds`);
    }
  }

  // --- Waypoints ---------------------------------------------------------
  const { nodes, edges } = map.waypoints;
  if (nodes.length === 0) {
    problems.push("waypoint graph has no nodes");
  } else {
    const seen = new Set<number>();
    const solidsNear = bucketSolids(map.solids);
    for (const n of nodes) {
      if (seen.has(n.id)) problems.push(`duplicate waypoint id ${n.id}`);
      seen.add(n.id);
      if (!containsPoint(bounds, n.pos)) {
        problems.push(`waypoint ${n.id} is outside bounds`);
        continue;
      }
      const chest: Vec3 = { x: n.pos.x, y: n.pos.y + 0.9, z: n.pos.z };
      for (const s of solidsNear(n.pos.x, n.pos.z)) {
        if (strictlyInside(s.box, n.pos) || strictlyInside(s.box, chest)) {
          problems.push(`waypoint ${n.id} is inside a solid`);
          break;
        }
      }
    }

    const adj = new Map<number, number[]>();
    for (const n of nodes) if (!adj.has(n.id)) adj.set(n.id, []);
    for (let i = 0; i < edges.length; i++) {
      const e = edges[i] as [number, number];
      const la = adj.get(e[0]);
      const lb = adj.get(e[1]);
      if (la === undefined || lb === undefined) {
        problems.push(`edge[${i}] references a missing node`);
        continue;
      }
      if (e[0] === e[1]) {
        problems.push(`edge[${i}] is a self-loop on node ${e[0]}`);
        continue;
      }
      la.push(e[1]);
      lb.push(e[0]);
    }

    // Connectivity: flood fill from the first node.
    const start = (nodes[0] as { id: number }).id;
    const reached = new Set<number>([start]);
    const queue: number[] = [start];
    for (let head = 0; head < queue.length; head++) {
      for (const nb of adj.get(queue[head] as number) as number[]) {
        if (reached.has(nb)) continue;
        reached.add(nb);
        queue.push(nb);
      }
    }
    if (reached.size !== adj.size) {
      const orphans = nodes
        .filter((n) => !reached.has(n.id))
        .map((n) => n.id)
        .slice(0, 8);
      problems.push(
        `waypoint graph is not connected (${adj.size - reached.size} unreachable, e.g. ${orphans.join(", ")})`,
      );
    }
  }

  // --- Visuals -----------------------------------------------------------
  const visuals = map.visuals;
  if (visuals !== undefined) {
    for (let i = 0; i < visuals.length; i++) {
      const v = visuals[i] as { pos: Vec3 };
      if (!containsPoint(bounds, v.pos)) {
        problems.push(`visuals[${i}] is outside bounds`);
      }
    }
  }

  return problems;
}
